import type { MemberRow } from "./members";

// PRD-001AE AE4: Individual Permissions.
//
// THIS TAB ASSIGNS A MEMBERSHIP ROLE AND NOTHING ELSE (AC-001AE.4). There is no per-user
// permission key here, no override table, and no second ACL. What a role may do lives in Group
// Permissions; who holds that role lives here. Both write through paths that already existed:
// Group through the AE2 matrix RPCs, Individual through `update_account_member_role`, the same
// call the Members directory has always made.
//
// Why it exists at all when Members can already change a role: Members is a list with a per-row
// editor, which answers "tell me about this person" well and "who are my admins" badly. This is
// the same capability presented as a roster grid, so the distribution is readable at a glance and
// a move is one cell rather than opening an editor.

/**
 * Roles a member can be moved INTO. This is the host's list, not a product preference:
 * `update_account_member_role` refuses anything else with "Role must be guard, supervisor, or
 * admin." Support is deliberately absent, which is the L4 note about hiding it from the tenant
 * Individual tab made concrete by the server.
 */
export const ASSIGNABLE_ROLES = ["guard", "supervisor", "admin"] as const;
export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];

/**
 * Columns the grid renders. Support is shown because a member may already hold it and hiding the
 * column would make that person look role-less, but its cells are never assignable.
 */
export const INDIVIDUAL_ROLE_COLUMNS = ["guard", "supervisor", "admin", "support"] as const;
export type IndividualRoleColumn = (typeof INDIVIDUAL_ROLE_COLUMNS)[number];

export const INDIVIDUAL_ROLE_LABELS: Readonly<Record<IndividualRoleColumn, string>> = {
  guard: "Mobile",
  supervisor: "Supervisor",
  admin: "Admin",
  support: "Support",
};

export function isAssignableRole(role: string): role is AssignableRole {
  return (ASSIGNABLE_ROLES as readonly string[]).includes(role);
}

export const SUPPORT_NOT_ASSIGNABLE_NOTE =
  "Support is a SecOps membership. It shows here when someone holds it, and it is not assignable from this workspace.";

export interface IndividualRow {
  readonly membershipId: string;
  /** Display name when the directory has one, else the token-safe member ref. Never a user id. */
  readonly label: string;
  readonly role: string;
  readonly status: string;
  /** Only an active membership can be moved. Suspended and removed are lifecycle states. */
  readonly assignable: boolean;
}

/**
 * The console cannot tell which row is the signed-in caller, and that is deliberate upstream:
 * `management_member_directory` withholds `user_id` and exposes only a hashed `member_ref`, so
 * there is nothing to compare a session against. The self-change refusal is therefore left to the
 * host, which raises this exact sentence; it is in the verbatim allowlist so the operator reads
 * the real reason rather than safe copy.
 */
export const SELF_ROLE_CHANGE_NOTE = "You cannot change your own role.";

export const INACTIVE_MEMBER_NOTE =
  "Only an active membership can change role. Restore this member on the Members list first.";

/**
 * Fold the member directory into roster rows.
 *
 * `memberRef` is the fallback label, never a raw user id: the directory hashes it for exactly this
 * reason. Rows are ordered admins first, then supervisors, then the rest, so the question this
 * grid exists to answer ("who can do the dangerous things") is at the top.
 */
export function buildIndividualRows(members: readonly MemberRow[]): IndividualRow[] {
  const rows = members.map((member) => ({
    membershipId: member.membershipId,
    label: member.memberDisplayName ?? member.memberRef ?? "Member",
    role: member.role,
    status: member.status,
    assignable: member.status === "active",
  }));

  const weight: Readonly<Record<string, number>> = {
    admin: 0,
    supervisor: 1,
    support: 2,
    guard: 3,
  };
  return rows.sort((left, right) => {
    const leftWeight = weight[left.role] ?? 4;
    const rightWeight = weight[right.role] ?? 4;
    if (leftWeight !== rightWeight) return leftWeight - rightWeight;
    return left.label.localeCompare(right.label);
  });
}

export type CellState =
  /** This member holds this role. Exactly one per row: the model is one role per membership. */
  | { readonly kind: "held" }
  /** Clicking moves the member into this role. */
  | { readonly kind: "assignable" }
  /** Rendered, never actionable, with a reason. */
  | { readonly kind: "locked"; readonly reason: string };

export const LAST_ADMIN_NOTE = "At least one active admin is required for this account.";

export interface CellContext {
  /** Every row currently loaded, for the last-admin mirror. */
  readonly rows: readonly IndividualRow[];
  /**
   * False when the directory page may have been truncated. The last-admin lock is SKIPPED then,
   * because an admin outside the page would make the count wrong and wrongly disable a legitimate
   * demotion. The host refuses either way; a wrong disable is the only failure the operator cannot
   * work around.
   */
  readonly rosterComplete: boolean;
}

/**
 * The state of one member-and-role cell.
 *
 * The refusals the host publishes for `update_account_member_role` are mirrored here so the
 * operator learns them from a disabled control rather than from a red banner after the click. The
 * host still refuses regardless; this is the affordance, not the control. The self-change refusal
 * is the one that cannot be mirrored, for the reason on SELF_ROLE_CHANGE_NOTE.
 */
export function cellState(
  row: IndividualRow,
  column: IndividualRoleColumn,
  context: CellContext,
): CellState {
  if (row.role === column) return { kind: "held" };
  if (column === "support") return { kind: "locked", reason: SUPPORT_NOT_ASSIGNABLE_NOTE };
  if (!row.assignable) return { kind: "locked", reason: INACTIVE_MEMBER_NOTE };
  if (context.rosterComplete && isLastActiveAdmin(context.rows, row)) {
    return { kind: "locked", reason: LAST_ADMIN_NOTE };
  }
  return { kind: "assignable" };
}

/** Held cells per column, for the header count line. */
export function roleCounts(
  rows: readonly IndividualRow[],
): Readonly<Record<IndividualRoleColumn, number>> {
  const counts: Record<IndividualRoleColumn, number> = {
    guard: 0,
    supervisor: 0,
    admin: 0,
    support: 0,
  };
  for (const row of rows) {
    if ((INDIVIDUAL_ROLE_COLUMNS as readonly string[]).includes(row.role)) {
      counts[row.role as IndividualRoleColumn] += 1;
    }
  }
  return counts;
}

/**
 * The host refuses removing the last active admin. Mirrored so the grid can disable the move
 * rather than let an operator discover it after the fact.
 */
export function isLastActiveAdmin(rows: readonly IndividualRow[], row: IndividualRow): boolean {
  if (row.role !== "admin" || !row.assignable) return false;
  return rows.filter((candidate) => candidate.role === "admin" && candidate.assignable).length <= 1;
}

/** Confirm copy for a move. Names both ends so nobody demotes the wrong person. */
export function roleChangeConfirmMessage(row: IndividualRow, next: IndividualRoleColumn): string {
  return `Move ${row.label} from ${INDIVIDUAL_ROLE_LABELS[row.role as IndividualRoleColumn] ?? row.role} to ${INDIVIDUAL_ROLE_LABELS[next]}? This changes what they can do everywhere, on the phone and in the console.`;
}

/** Search over the member label only. Roles are columns, not searchable text. */
export function filterIndividualRows(
  rows: readonly IndividualRow[],
  query: string,
): IndividualRow[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return [...rows];
  return rows.filter((row) => row.label.toLowerCase().includes(needle));
}
